import { useEffect, useRef, useState } from "react";
import { viewerKeyAction, wheelZoomFactor } from "utils/viewerInput";

// Browser plumbing for the Field Cameras viewer: wheel/pinch zoom that never scrolls the page,
// keyboard shortcuts while the viewer is hovered or focused, and fullscreen. Elements are
// passed in (from callback refs), so the listeners follow the element if it is re-created.

/**
 * Wheel and trackpad pinch over `element` call `onZoom(factor, clientX, clientY)` and never
 * scroll or zoom the page. React's onWheel is passive and cannot preventDefault, hence the
 * native listener. Chrome, Edge and Firefox deliver pinch as ctrlKey wheel events; Safari
 * delivers gesture events. While `enabled` is false (nothing to zoom) the page scrolls as usual.
 */
export function useWheelZoom(element, enabled, onZoom) {
  const latest = useRef({ enabled, onZoom });
  useEffect(() => {
    latest.current = { enabled, onZoom };
  });
  useEffect(() => {
    if (!element) return undefined;
    let gestureScale = null; // Safari pinch in progress: the scale at its last event
    const wheel = (e) => {
      if (!latest.current.enabled) return;
      e.preventDefault();
      if (gestureScale === null)
        latest.current.onZoom(wheelZoomFactor(e), e.clientX, e.clientY);
    };
    const gestureStart = (e) => {
      if (!latest.current.enabled) return;
      e.preventDefault();
      gestureScale = e.scale || 1;
    };
    const gestureChange = (e) => {
      if (gestureScale === null) return;
      e.preventDefault();
      const scale = e.scale || 1;
      latest.current.onZoom(scale / gestureScale, e.clientX, e.clientY);
      gestureScale = scale;
    };
    const gestureEnd = (e) => {
      if (gestureScale === null) return;
      e.preventDefault();
      gestureScale = null;
    };
    element.addEventListener("wheel", wheel, { passive: false });
    element.addEventListener("gesturestart", gestureStart);
    element.addEventListener("gesturechange", gestureChange);
    element.addEventListener("gestureend", gestureEnd);
    return () => {
      element.removeEventListener("wheel", wheel, { passive: false });
      element.removeEventListener("gesturestart", gestureStart);
      element.removeEventListener("gesturechange", gestureChange);
      element.removeEventListener("gestureend", gestureEnd);
    };
  }, [element]);
}

const REPEATING = new Set(["previous", "next"]);

/**
 * Runs `actions[viewerKeyAction(event)]` for keydowns while the pointer is over `element` or
 * focus is inside it. A view that handles a key itself (the panorama's Shift+arrows) calls
 * preventDefault first and is left alone. Held keys repeat stepping, not toggling.
 */
export function useViewerKeys(element, actions) {
  const latest = useRef(actions);
  useEffect(() => {
    latest.current = actions;
  });
  useEffect(() => {
    if (!element) return undefined;
    let hovered = false;
    const enter = () => {
      hovered = true;
    };
    const leave = () => {
      hovered = false;
    };
    const keydown = (e) => {
      if (e.defaultPrevented) return;
      if (!hovered && !element.contains(document.activeElement)) return;
      const action = viewerKeyAction(e);
      const run = action && latest.current[action];
      if (!run) return;
      e.preventDefault();
      if (!e.repeat || REPEATING.has(action)) run();
    };
    element.addEventListener("pointerenter", enter);
    element.addEventListener("pointerleave", leave);
    document.addEventListener("keydown", keydown);
    return () => {
      element.removeEventListener("pointerenter", enter);
      element.removeEventListener("pointerleave", leave);
      document.removeEventListener("keydown", keydown);
    };
  }, [element]);
}

const fullscreenElement = () =>
  document.fullscreenElement || document.webkitFullscreenElement || null;

const exitNativeFullscreen = () => {
  const exit = document.exitFullscreen || document.webkitExitFullscreen;
  try {
    exit?.call(document)?.catch?.(() => {});
  } catch {
    // Already left fullscreen.
  }
};

/**
 * Fullscreen for `element` through the Fullscreen API, or a fixed in-page overlay where the API
 * is missing or refused (e.g. iPhone Safari). The browser exits real fullscreen on Esc; the
 * overlay listens for Esc itself and stops the page behind it from scrolling.
 */
export function useFullscreen(element) {
  const [native, setNative] = useState(false);
  const [overlay, setOverlay] = useState(false);
  useEffect(() => {
    const sync = () => setNative(!!element && fullscreenElement() === element);
    sync();
    document.addEventListener("fullscreenchange", sync);
    document.addEventListener("webkitfullscreenchange", sync);
    return () => {
      document.removeEventListener("fullscreenchange", sync);
      document.removeEventListener("webkitfullscreenchange", sync);
    };
  }, [element]);
  useEffect(() => {
    if (!overlay) return undefined;
    const keydown = (e) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      setOverlay(false);
    };
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", keydown);
    return () => {
      document.removeEventListener("keydown", keydown);
      document.body.style.overflow = overflow;
    };
  }, [overlay]);
  const enter = async () => {
    const request = element?.requestFullscreen || element?.webkitRequestFullscreen;
    if (request) {
      try {
        await request.call(element);
        return;
      } catch {
        // Refused here (permissions, iframe): fall back to the overlay.
      }
    }
    setOverlay(true);
  };
  const exit = () => {
    setOverlay(false);
    if (element && fullscreenElement() === element) exitNativeFullscreen();
  };
  const active = native || overlay;
  return { active, toggle: () => (active ? exit() : enter()), exit };
}
